// ===========================================================================
// World Simulation Module
// Manages agents (people and weapons) in a 2D world
// ===========================================================================

import {
  AgentPerson,
  AgentWeapon,
  WorldState,
  WORLD_WIDTH,
  WORLD_HEIGHT,
  PERSON_RADIUS,
} from '../types';

let idCounter = 0;
const generateId = (prefix: string) => `${prefix}_${++idCounter}`;

/**
 * Create initial empty world state
 */
export function createWorld(): WorldState {
  return {
    people: [],
    weapons: [],
    frameIndex: 0,
    running: false,
    selectedAgentId: null,
    dragOffset: null,
  };
}

/**
 * Create a new person agent
 */
export function createPerson(
  x: number,
  y: number,
  vx: number = 0,
  vy: number = 0
): AgentPerson {
  return {
    id: generateId('person'),
    x: clamp(x, PERSON_RADIUS, WORLD_WIDTH - PERSON_RADIUS),
    y: clamp(y, PERSON_RADIUS, WORLD_HEIGHT - PERSON_RADIUS),
    vx,
    vy,
    radius: PERSON_RADIUS,
    baseLabel: 'person',
    confBase: 0.85 + Math.random() * 0.15, // 0.85-1.0
    suspectFrames: 0,
    suspectPinFrames: 0,
    isSuspect: false,
  };
}

/**
 * Create a new weapon
 */
export function createWeapon(x: number, y: number, attachedTo?: string): AgentWeapon {
  return {
    id: generateId('weapon'),
    x,
    y,
    attachedTo,
  };
}

/**
 * Add a person to the world
 */
export function addPerson(world: WorldState, x: number, y: number): WorldState {
  const person = createPerson(x, y);
  return {
    ...world,
    people: [...world.people, person],
  };
}

/**
 * Add a weapon to the world
 */
export function addWeapon(
  world: WorldState,
  x: number,
  y: number,
  attachToNearest: boolean = false
): WorldState {
  let attachedTo: string | undefined;
  let wx = x;
  let wy = y;

  if (attachToNearest && world.people.length > 0) {
    const nearest = findNearestPerson(world, x, y);
    if (nearest && distance(x, y, nearest.x, nearest.y) < 100) {
      attachedTo = nearest.id;
      wx = nearest.x;
      wy = nearest.y;
    }
  }

  const weapon = createWeapon(wx, wy, attachedTo);
  return {
    ...world,
    weapons: [...world.weapons, weapon],
  };
}

/**
 * Remove an agent by ID
 */
export function removeAgent(world: WorldState, agentId: string): WorldState {
  return {
    ...world,
    people: world.people.filter((p) => p.id !== agentId),
    weapons: world.weapons.filter((w) => w.id !== agentId && w.attachedTo !== agentId),
    selectedAgentId: world.selectedAgentId === agentId ? null : world.selectedAgentId,
  };
}

/**
 * Find nearest person to a point
 */
export function findNearestPerson(
  world: WorldState,
  x: number,
  y: number
): AgentPerson | null {
  let nearest: AgentPerson | null = null;
  let minDist = Infinity;

  for (const person of world.people) {
    const d = distance(x, y, person.x, person.y);
    if (d < minDist) {
      minDist = d;
      nearest = person;
    }
  }

  return nearest;
}

/**
 * Find nearest weapon to a point
 */
export function findNearestWeapon(
  world: WorldState,
  x: number,
  y: number
): AgentWeapon | null {
  let nearest: AgentWeapon | null = null;
  let minDist = Infinity;

  for (const weapon of world.weapons) {
    const d = distance(x, y, weapon.x, weapon.y);
    if (d < minDist) {
      minDist = d;
      nearest = weapon;
    }
  }

  return nearest;
}

/**
 * Find any agent (person or weapon) at a point
 */
export function findAgentAt(
  world: WorldState,
  x: number,
  y: number,
  threshold: number = 25
): { type: 'person' | 'weapon'; agent: AgentPerson | AgentWeapon } | null {
  // Check people first
  for (const person of world.people) {
    if (distance(x, y, person.x, person.y) < threshold) {
      return { type: 'person', agent: person };
    }
  }

  // Check weapons
  for (const weapon of world.weapons) {
    if (distance(x, y, weapon.x, weapon.y) < threshold) {
      return { type: 'weapon', agent: weapon };
    }
  }

  return null;
}

/**
 * Move an agent to a new position
 */
export function moveAgent(
  world: WorldState,
  agentId: string,
  newX: number,
  newY: number
): WorldState {
  const x = clamp(newX, PERSON_RADIUS, WORLD_WIDTH - PERSON_RADIUS);
  const y = clamp(newY, PERSON_RADIUS, WORLD_HEIGHT - PERSON_RADIUS);

  return {
    ...world,
    people: world.people.map((p) =>
      p.id === agentId ? { ...p, x, y } : p
    ),
    weapons: world.weapons.map((w) =>
      w.id === agentId ? { ...w, x, y } : w
    ),
  };
}

/**
 * Update world simulation by one step
 */
export function stepWorld(world: WorldState, dt: number, speedMultiplier: number): WorldState {
  // Update people positions
  const updatedPeople = world.people.map((person) => {
    let { x, y, vx, vy } = person;

    // Apply velocity
    x += vx * dt * speedMultiplier;
    y += vy * dt * speedMultiplier;

    // Bounce off boundaries
    if (x < PERSON_RADIUS) {
      x = PERSON_RADIUS;
      vx = Math.abs(vx);
    }
    if (x > WORLD_WIDTH - PERSON_RADIUS) {
      x = WORLD_WIDTH - PERSON_RADIUS;
      vx = -Math.abs(vx);
    }
    if (y < PERSON_RADIUS) {
      y = PERSON_RADIUS;
      vy = Math.abs(vy);
    }
    if (y > WORLD_HEIGHT - PERSON_RADIUS) {
      y = WORLD_HEIGHT - PERSON_RADIUS;
      vy = -Math.abs(vy);
    }

    // Random velocity changes (brownian motion)
    if (Math.random() < 0.02) {
      vx += (Math.random() - 0.5) * 0.5;
      vy += (Math.random() - 0.5) * 0.5;
    }

    // Damping
    vx *= 0.99;
    vy *= 0.99;

    return { ...person, x, y, vx, vy };
  });

  // Update attached weapons to follow their owners
  const updatedWeapons = world.weapons.map((weapon) => {
    if (weapon.attachedTo) {
      const owner = updatedPeople.find((p) => p.id === weapon.attachedTo);
      if (owner) {
        return { ...weapon, x: owner.x + 10, y: owner.y + 10 };
      }
    }
    return weapon;
  });

  return {
    ...world,
    people: updatedPeople,
    weapons: updatedWeapons,
    frameIndex: world.frameIndex + 1,
  };
}

/**
 * Spawn a crowd preset (clustered people)
 */
export function spawnCrowd(
  world: WorldState,
  centerX: number,
  centerY: number,
  count: number,
  spread: number
): WorldState {
  let newWorld = { ...world, people: [...world.people] };

  for (let i = 0; i < count; i++) {
    const angle = Math.random() * Math.PI * 2;
    const radius = Math.random() * spread;
    const x = centerX + Math.cos(angle) * radius;
    const y = centerY + Math.sin(angle) * radius;
    const vx = (Math.random() - 0.5) * 0.3;
    const vy = (Math.random() - 0.5) * 0.3;
    const person = createPerson(x, y, vx, vy);
    newWorld.people.push(person);
  }

  return newWorld;
}

/**
 * Spawn random people across the world
 */
export function spawnRandom(world: WorldState, count: number): WorldState {
  let newWorld = { ...world, people: [...world.people] };

  for (let i = 0; i < count; i++) {
    const x = PERSON_RADIUS + Math.random() * (WORLD_WIDTH - 2 * PERSON_RADIUS);
    const y = PERSON_RADIUS + Math.random() * (WORLD_HEIGHT - 2 * PERSON_RADIUS);
    const vx = (Math.random() - 0.5) * 1;
    const vy = (Math.random() - 0.5) * 1;
    const person = createPerson(x, y, vx, vy);
    newWorld.people.push(person);
  }

  return newWorld;
}

/**
 * Spawn a line of people
 */
export function spawnLine(
  world: WorldState,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  count: number
): WorldState {
  let newWorld = { ...world, people: [...world.people] };

  for (let i = 0; i < count; i++) {
    const t = count > 1 ? i / (count - 1) : 0.5;
    const x = x1 + (x2 - x1) * t;
    const y = y1 + (y2 - y1) * t;
    const person = createPerson(x, y);
    newWorld.people.push(person);
  }

  return newWorld;
}

/**
 * Reset the world to empty state
 */
export function resetWorld(): WorldState {
  idCounter = 0;
  return createWorld();
}

// ----------------------------- Utility Functions ---------------------------

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function distance(x1: number, y1: number, x2: number, y2: number): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  return Math.sqrt(dx * dx + dy * dy);
}

export { clamp, distance };
